// The shell's state: the bootstrap payload, the shared filters, and the theme. Components
// subscribe through StoreMixin and re-render when any of it changes.
//
// Filters live here rather than in each view because PRD 7.2 makes them shared: the same filter
// set follows the planner from view to view. The URL is their home (see router.js); the store
// holds the parsed copy the components read.

import { defaultFilters } from './lib/filters.js';
import { EXPERT_KEY } from './lib/expert.js';

const THEME_KEY = 'waag.theme';

const initialState = () => ({
  ready: false, // the bootstrap request has settled, one way or the other
  error: null, // what went wrong loading the shell, if anything
  user: null,
  scope: null,
  tools: [], // the tools this user may enter; the eyebrow switcher renders from it
  // The Estimate tool's own bootstrap (Estimate Tool 2.4), loaded when the reader first enters
  // that tool rather than on every page load: the Forecast tool never reads it.
  estimate: { ready: false, loading: false, error: null, data: null },
  run: null,
  runs: [],
  // The saved scenarios (Phase 35). The banner needs the selected one's name on every page, so
  // the shell carries the list rather than each page fetching it.
  scenarios: [],
  sources: [],
  vocabularies: { project_types: [], services: [], departments: [], offices: [], people: [] },
  palette: [],
  settings: null,
  filters: defaultFilters(),
  theme: readTheme(), // 'system' | 'light' | 'dark'
  // Expert mode (lib/expert.js): every Forecast view and every filter, or the everyday five and
  // the window controls. Off by default; a view preference like the theme, and kept beside it.
  expert: readExpert(),
});

function readExpert() {
  try {
    return localStorage.getItem(EXPERT_KEY) === 'on';
  } catch {
    return false;
  }
}

function readTheme() {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

let state = initialState();
const listeners = new Set();

function notify() {
  for (const fn of listeners) fn(state);
}

export const store = {
  get() {
    return state;
  },

  set(partial) {
    state = { ...state, ...partial };
    notify();
  },

  /** Patch the Estimate tool's slice without disturbing the rest. */
  setEstimate(partial) {
    state = { ...state, estimate: { ...state.estimate, ...partial } };
    notify();
  },

  /** Replace the filter state (the router owns when this happens). */
  setFilters(filters) {
    state = { ...state, filters };
    notify();
  },

  /** Persist and apply the theme. `system` follows the OS and stamps nothing. */
  setTheme(theme) {
    state = { ...state, theme };
    applyTheme(theme);
    try {
      if (theme === 'system') localStorage.removeItem(THEME_KEY);
      else localStorage.setItem(THEME_KEY, theme);
    } catch {
      // A browser with storage blocked still gets the theme for this page view.
    }
    notify();
  },

  /** Persist and apply expert mode. Off is the default, so off is stored as nothing. */
  setExpert(on) {
    const expert = Boolean(on);
    state = { ...state, expert };
    try {
      if (expert) localStorage.setItem(EXPERT_KEY, 'on');
      else localStorage.removeItem(EXPERT_KEY);
    } catch {
      // A browser with storage blocked still gets the mode for this page view.
    }
    notify();
  },

  clear() {
    state = { ...initialState(), ready: true };
    notify();
  },

  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

/** Stamp (or clear) the theme attribute the token blocks in index.html key off. */
export function applyTheme(theme = store.get().theme) {
  const root = document.documentElement;
  if (theme === 'light' || theme === 'dark') root.setAttribute('data-theme', theme);
  else root.removeAttribute('data-theme');
}

/** Mixin: re-render a Lit component whenever the store changes. */
export const StoreMixin = (Base) =>
  class extends Base {
    connectedCallback() {
      super.connectedCallback?.();
      this._unsubscribe = store.subscribe(() => this.requestUpdate());
    }

    disconnectedCallback() {
      super.disconnectedCallback?.();
      this._unsubscribe?.();
    }

    get appState() {
      return store.get();
    }
  };
